// Cursor-following unit info bubble. Shown when hovering a unit sprite.
// Same positioning approach as TileTooltip (global mousemove + fixed pos).

import type { SceneNode } from '@aod/protocol';
import { playerColor } from '@aod/ui';
import { useEffect, useState } from 'react';
import { useT } from '../i18n/index.js';
import { houseName } from '../sea/houseNames.js';
import { merchantColour, merchantHouseOf } from '../sea/merchantModel.js';

interface UnitTooltipProps {
  readonly unit: SceneNode | null;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function UnitTooltip({ unit }: UnitTooltipProps) {
  const t = useT();
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => setPos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  if (!unit || !pos) return null;
  const kind = (unit.props?.kind as string) ?? 'unit';
  const ownerId = unit.props?.ownerId as number | undefined;
  const hp = (unit.props?.hp as number) ?? 0;
  const hpMax = (unit.props?.hpMax as number) ?? 0;
  const remaining = unit.props?.remainingMovement as number | undefined;
  const maxMovement = unit.props?.maxMovement as number | undefined;
  const isHero = Array.isArray(unit.tags) && unit.tags.includes('hero');
  // A foreign house's vessel wears its house's colours, not a player's.
  const house = merchantHouseOf(ownerId, unit.props?.seaHouse as number | undefined);
  const hpPct = hpMax > 0 ? Math.max(0, hp / hpMax) : 0;

  return (
    <div
      style={{ left: pos.x + 14, top: pos.y + 14 }}
      className="pointer-events-none fixed z-[200] min-w-[9rem] rounded-md border border-border/70 bg-popover/95 px-2.5 py-1.5 text-[11px] leading-snug text-popover-foreground shadow-lg backdrop-blur-sm"
    >
      <div className="flex items-center gap-1.5 font-semibold">
        <span
          aria-hidden
          className="inline-block size-2 rounded-full"
          style={{ background: house !== null ? merchantColour(house) : playerColor(ownerId) }}
        />
        <span className="uppercase tracking-[0.12em]">
          {house !== null ? t(houseName(house)) : capitalize(kind)}
        </span>
        {isHero && (
          <span className="rounded-sm bg-amber-500/20 px-1 text-[9px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
            Hero
          </span>
        )}
      </div>
      <div className="mt-0.5 flex items-center gap-1.5">
        <div className="h-1 w-16 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full"
            style={{
              width: `${hpPct * 100}%`,
              background: hpPct > 0.5 ? '#44cc44' : hpPct > 0.25 ? '#ccaa44' : '#cc4444',
            }}
          />
        </div>
        <span className="font-mono text-muted-foreground">
          {hp}/{hpMax}
        </span>
      </div>
      {remaining !== undefined && maxMovement !== undefined && (
        <div className="mt-0.5 text-muted-foreground">
          Movement {remaining}/{maxMovement}
        </div>
      )}
      <div className="text-muted-foreground">
        {unit.props?.q !== undefined && `q=${unit.props.q} r=${unit.props.r}`}
      </div>
    </div>
  );
}
